import { all, call, put, select as sagaSelect } from 'redux-saga/effects';

import * as meetingSimpleResourceService from '../../../resource/meeting-simple/meeting-simple.saga-service';
import * as scoreboardResourceService from '../../../resource/kpi-row-scoreboard/kpi-row-scoreboard.saga-service';
import * as scoreResourceService from '../../../resource/score/score.saga-service';

import * as kpiService from '../../../resource/kpi/kpi.saga-service';
import * as kpiRowService from '../../../resource/kpi-row/kpi-row.saga-service';
import * as kpiRowScoreboardService from '../../../resource/kpi-row-scoreboard/kpi-row-scoreboard.saga-service';

import * as dialogService from '../../../dialog/dialog.saga-services';
import { apiRequest } from '../../../api/api.saga-services';

import * as actions from './kpi.actions';
import * as sessionActions from '../sessions/sessions.actions';
import * as scoreApi from '../../../api/request-config/score.api';
import { ISession } from '../../../resource/session/session.types';
import { IKpiRow, IKpiRowScoreOrderSocketPayload } from '../../../resource/kpi-row/kpi-row.types';
import {
  IKpiRowScoreboard,
  IKpiRowScoreboardSocketPayload,
} from '../../../resource/kpi-row-scoreboard/kpi-row-scoreboard.types';
import { select, selectWithParams } from '../../../../utils/selector.utils';
import { kpiSelectors } from '../../../resource/kpi/kpi.selectors';
import { IKpi } from '../../../resource/kpi/kpi.types';
import { omit } from 'lodash';
import { kpiRowSelectors } from '../../../resource/kpi-row/kpi-row.selectors';
import {
  IKpiRowScoreSocketPayload,
  IKpiRowScoreArraySocketPayload,
  IScore,
  IScoreCreate,
  IScoreCreateForm,
} from '../../../resource/score/score.types';
import { kpiPageSelectors } from './kpi.selectors';
import { scoreSelectors } from '../../../resource/score/score.selectors';
import { IMeetingSimple } from '../../../resource/meeting-simple/meeting-simple.types';
import { sessionSelectors } from '../../../resource/session/session.selectors';
import { meetingSimpleSelectors } from '../../../resource/meeting-simple/meeting-simple.selectors';
import moment from 'moment';
import { authSelectors } from '../../../auth/auth.selectors';
import { IUser } from '../../../resource/user/user.types';
import { IKpiSearchParams } from './kpi.types';

export function* storeKpiRowIds(kpiRowIds: IKpiRow['id'][]) {
  yield kpiRowIds ? put(actions.storeKpiRowIds(kpiRowIds)) : put(actions.clearKpiRowIds());
}

export function* storeSessionIds(sessionIds: ISession['id'][]) {
  yield sessionIds ? put(actions.storeSessionIds(sessionIds)) : put(actions.clearSessionIds());
  yield put(sessionActions.storeSessionIds(sessionIds));
}

export function* storeScoreIds(scoreIds: IScore['id'][]) {
  yield scoreIds ? put(actions.storeScoreIds(scoreIds)) : put(actions.clearScoreIds());
}

export function* storeIsFiltered(isFiltered: boolean) {
  yield isFiltered ? put(actions.storeIsFiltered(isFiltered)) : put(actions.clearIsFiltered());
}

export function* findNextSession(sessionIds: ISession['id'][]) {
  const sessions = (yield selectWithParams(sessionSelectors.selectResourcesById, sessionIds)) as ISession[];
  /**
   * @todo: This function is comparing UTC dates with dates created for user in their timezone, which is not right.
   * Currently is not used in any sensitive place, but it should be fixed or removed in the future
   */
  const nextSession = sessions.find((session) => new Date(session.sessionDate) > new Date()) as ISession;
  if (nextSession) {
    yield put(sessionActions.storeNextSession(nextSession.id));
  }
}

export function* fetchKpi(meetingId: IMeetingSimple['id'], queryParams?: IKpiSearchParams) {
  if (queryParams) {
    if (queryParams.dateSince) {
      queryParams.dateSince = moment(queryParams.dateSince).format('YYYY-MM-DD');
    }

    if (queryParams.dateUntil) {
      queryParams.dateUntil = moment(queryParams.dateUntil).format('YYYY-MM-DD');
    }
  }

  const meeting = (yield call(meetingSimpleResourceService.getKpis, meetingId, queryParams)) as IMeetingSimple;

  if (!meeting || !meeting.kpiId) {
    return;
  }

  const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meeting.kpiId)) as IKpi;

  yield call(storeScoreIds, kpi.kpiRowIds);
  yield call(storeKpiRowIds, kpi.kpiRowIds);
  yield call(storeSessionIds, kpi.sessionIds);
  yield call(storeIsFiltered, true);
}

export function* setupKpi(meetingId: IMeetingSimple['id']) {
  const meeting = (yield call(meetingSimpleResourceService.getKpis, meetingId)) as IMeetingSimple;

  if (!meeting || !meeting.kpiId) {
    return;
  }

  const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meeting.kpiId)) as IKpi;

  yield call(storeScoreIds, kpi.kpiRowIds);
  yield call(storeKpiRowIds, kpi.kpiRowIds);
  yield call(storeSessionIds, kpi.sessionIds);

  yield put(sessionActions.storeNextSession(meeting.nextSessionId));
  yield put(sessionActions.storeLastSession(meeting.lastSessionId));
}

export function* saveScoreboard(scoreboardInfo: IKpiRowScoreboard) {
  if (!scoreboardInfo) {
    return;
  }
  if ((scoreboardInfo.id + '').includes('/')) {
    const scoreboard = (yield call(
      scoreboardResourceService.createKpiRowScoreboard,
      omit(scoreboardInfo, 'id'),
    )) as IKpiRowScoreboard;

    //yield call(scoreboardResourceService.clearKpiRowScoreboard, scoreboardInfo.id);
    //yield call(scoreboardResourceService.storeKpiRowScoreboard, scoreboard);

    const kpiRow = (yield selectWithParams(kpiRowSelectors.selectResourceById, scoreboardInfo.scoresId)) as IKpiRow;

    if (kpiRow) {
      kpiRow.kpiRowScoreboardIds[kpiRow.kpiRowScoreboardIds.indexOf(scoreboardInfo.id)] = scoreboard.id;
      yield call(kpiRowService.storeKpiRow, { ...kpiRow });
    }
  } else {
    yield call(scoreboardResourceService.updateKpiRowScoreboard, scoreboardInfo);
    //yield call(scoreboardResourceService.storeKpiRowScoreboard, { ...scoreboard, id: scoreboardInfo.id });
  }
  yield call(dialogService.closeDialog, 'kpiNoteFormDialog');
}

// TODO: Ask Arsen about handling this from notifications - whole table should be generated on the backend because i cant find
// which item has been updated
export function* handleScoreboardFromNotification(scoreboard: IKpiRowScoreboardSocketPayload) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];

  if (String(scoreboard.actionUserId) == loggedUserId) {
    return;
  }

  /**
   * @todo: insert a validation to only execute this function is the user is not the same as the one who inserted the KPI
   */
  const kpiRow = (yield selectWithParams(kpiRowSelectors.selectResourceById, scoreboard.scoresId)) as IKpiRow;
  if (!kpiRow) {
    return;
  }

  const exist =
    kpiRow.kpiRowScoreboardIds[kpiRow.kpiRowScoreboardIds.indexOf(`${scoreboard.scoresId}/${scoreboard.sessionId}`)];
  // If true it means KpiRowScoreboard id is of the kind "scoreId/sessionId", so id will be overriden now with an int value of id.
  if (exist) {
    kpiRow.kpiRowScoreboardIds[kpiRow.kpiRowScoreboardIds.indexOf(`${scoreboard.scoresId}/${scoreboard.sessionId}`)] =
      scoreboard.id;
  }

  yield call(kpiRowService.storeKpiRow, { ...kpiRow });
  yield call(scoreboardResourceService.storeKpiRowScoreboard, scoreboard);
}

export function* createScore(scoreInfo: IScoreCreateForm) {
  const score = (yield call(scoreResourceService.createScore, mapScoreFormToScoreCreate(scoreInfo))) as IScore;

  if (!score) {
    return;
  }

  yield call(generateAndStoreKpiRowFromScore, score);
  yield call(dialogService.closeDialog, 'scoreFormDialog');
}

export function* handleScoreFromNotification(payload: IKpiRowScoreSocketPayload | IKpiRowScoreArraySocketPayload) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];

  // Handle single score
  if ('scores' in payload) {
    // Handle array of scores
    if (String(payload.actionUserId) === loggedUserId) {
      return;
    }

    if (payload.scores.length === 0) {
      return;
    }

    // Process all scores in parallel
    yield all(
      payload.scores.map((score) =>
        call(function* () {
          const scoreExists = (yield selectWithParams(scoreSelectors.selectResourceById, score.id)) as IScore | null;
          yield call(scoreResourceService.storeScore, score);

          const kpiRow = (yield selectWithParams(kpiRowSelectors.selectResourceById, score.id)) as IKpiRow;
          if (kpiRow) {
            yield call(kpiRowService.storeKpiRow, { ...kpiRow, userId: score.userId });
          }

          if (!scoreExists) {
            yield call(generateAndStoreKpiRowFromScore, score);
          }
        }),
      ),
    );
  } else {
    // Handle single score
    if (String(payload.actionUserId) === loggedUserId) {
      return;
    }

    const scoreExists = (yield selectWithParams(scoreSelectors.selectResourceById, payload.id)) as IScore | null;
    yield call(scoreResourceService.storeScore, payload);

    const kpiRow = (yield selectWithParams(kpiRowSelectors.selectResourceById, payload.id)) as IKpiRow;
    if (kpiRow) {
      yield call(kpiRowService.storeKpiRow, { ...kpiRow, userId: payload.userId });
    }

    if (scoreExists) {
      return;
    }

    yield call(generateAndStoreKpiRowFromScore, payload);
  }
}

export function* handleDeleteScoreFromNotification(score: IKpiRowScoreSocketPayload) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(score.actionUserId) == loggedUserId) {
    return;
  }
  yield call(scoreResourceService.clearScore, score.id);
  yield call(kpiRowService.clearKpiRow, score.id);
}

export function* updateScore(scoreId: IScore['id'], scoreInfo: IScoreCreateForm) {
  const score = (yield call(scoreResourceService.updateScore, {
    id: scoreId,
    ...mapScoreFormToScoreCreate(scoreInfo),
  })) as IScore;

  if (!score) {
    return;
  }

  yield call(dialogService.closeDialog, 'scoreFormDialog');
}

export function* updateScoresBulk(scoreIds: IScore['id'][], userId?: string) {
  yield call(scoreResourceService.updateScoresBulk, scoreIds, userId);
}

export function* deleteScore(scoreId: IScore['id']) {
  const res = (yield call(apiRequest, scoreApi.deleteScoreApi(scoreId))) as unknown;
  if (!res) {
    return;
  }

  const score = (yield selectWithParams(scoreSelectors.selectResourceById, scoreId)) as IScore;
  const meetingId = score.meetingId;
  const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meetingId)) as IKpi;

  const scoresIds = (yield select(kpiPageSelectors.selectScoreIds)) as IScore['id'][];
  const scoresIdsWithOutDeletedScoreId = scoresIds.filter((id) => id !== scoreId);

  kpi.kpiRowIds = scoresIdsWithOutDeletedScoreId;

  yield call(kpiService.storeKpi, kpi);
  yield call(storeKpiRowIds, scoresIdsWithOutDeletedScoreId);
  yield call(storeScoreIds, scoresIdsWithOutDeletedScoreId);
  yield call(scoreResourceService.clearScore, scoreId);
  yield call(kpiRowService.clearKpiRow, scoreId);
}

export function* saveScoreOrder(scoreIds: IScore['id'][]) {
  yield call(scoreResourceService.saveScoreOrder, { scoreIds });
  yield call(storeScoreIds, scoreIds);
  yield call(storeKpiRowIds, scoreIds);
  yield call(kpiRowService.update, scoreIds);
}

export function* saveScoreOrderFromNotification(payload: IKpiRowScoreOrderSocketPayload) {
  const { scoreIds, actionUserId } = payload;
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(actionUserId) == loggedUserId) {
    return;
  }

  yield call(kpiRowService.update, scoreIds);
  yield call(storeScoreIds, scoreIds);
  yield call(storeKpiRowIds, scoreIds);
}

export function* generateAndStoreKpiRowFromScore(score: IScore) {
  const meeting = (yield selectWithParams(
    meetingSimpleSelectors.selectResourceById,
    score.meetingId,
  )) as IMeetingSimple;
  const sessionIds = (yield sagaSelect(kpiPageSelectors.selectSessionIds)) as ISession['id'][];

  if (!meeting || !sessionIds) {
    return;
  }

  const kpiRow = generateKpiRow(score, sessionIds ? sessionIds : []);

  yield call(kpiRowScoreboardService.storeKpiRowScoreboard, kpiRow.kpiRowScoreboards);

  yield call(kpiRowService.storeKpiRow, {
    id: kpiRow.id,
    scoreId: kpiRow.id,
    userId: kpiRow.userId,
    kpiRowScoreboardIds: kpiRow.kpiRowScoreboards.map((kpiRowScoreboard) => kpiRowScoreboard.id),
  });

  if (meeting.kpiId) {
    const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meeting.kpiId)) as IKpi;
    kpi.kpiRowIds = [...kpi.kpiRowIds, kpiRow.id];

    yield call(kpiService.storeKpi, { ...kpi });
    yield call(storeKpiRowIds, kpi.kpiRowIds);
    yield call(storeScoreIds, kpi.kpiRowIds);
  }
}

const generateKpiRow = (score: IScore, sessionIds: ISession['id'][]) => ({
  id: score.id,
  scoreId: score.id,
  userId: score.userId,
  kpiRowScoreboards: sessionIds.map((sessionId) => ({
    id: `${score.id}/${sessionId}`,
    sessionId: sessionId,
    scoresId: score.id,
    measured: '',
    note: '',
  })),
});

export const mapScoreFormToScoreCreate = (scoreInfo: IScoreCreateForm): IScoreCreate => ({
  meetingId: scoreInfo.meeting.value,
  userId: scoreInfo.user.value,
  title: scoreInfo.title,
  goal: scoreInfo.goal.value,
  score: scoreInfo.score,
  units: scoreInfo.units,
  type: scoreInfo.type.value,
});

export function* clearKpisPageStorage() {
  yield put(actions.clearSessionIds());
  yield put(actions.clearKpiRowIds());
  yield put(actions.clearScoreIds());
}
